import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as action from '../../Action/Curation/Curation_Action';
import * as Authservices from '../../Services/services';
import { addAt, buildSavePayload, moveToRank, removeRow } from '../../utils/trailerRanking';
import { usePermission } from '../../hooks/usePermission';
import { PERM } from '../../config/permissions';
import { CuratedCollectionStyles } from './CuratedCollection';
import { NewOnHikeCinemaStyles, Thumb } from './NewOnHikeCinema';

/**
 * Trailer Ranking — the order of trailers in the app's Explore feed, shown as a tab on /curated.
 *
 * Backed by the generic curation resource with curation_type=trailer_ranking:
 *   GET /curation/curate-collections → { id, explore_limit, items[] (the list, ranked), available[] (removed) }
 *   PUT /curation/curate-collections → { series_ids, removed_ids } to save, or { reset: true } for the default order
 *
 * The list on screen is the Explore list, at most `explore_limit` rows. Add Series
 * puts a removed series at a position (a full list drops its last row); Remove takes
 * a row out and nothing moves up. Nothing goes live until Save & Publish.
 */

const CURATION_TYPE = 'trailer_ranking';

/** Unwraps the shared response envelope: { success, message, data, ... } → data. */
const unwrap = (res) => (res?.data !== undefined ? res.data : res?.responseDetails);

const readMessage = (res, fallback) => res?.responseMessage || res?.message || fallback;

const readError = (err, fallback) => {
    const details = Array.isArray(err?.details?.fields) && err.details.fields.length > 0
        ? err.details.fields.map((d) => d?.message || d).join('; ')
        : null;
    return details || readMessage(err, fallback);
};

const fromItem = (item) => ({
    seriesId: Number(item?.series_id),
    title: item?.series_title || '',
    thumbnail: item?.series_thumbnail || '',
    publishAt: item?.publish_at || null,
    isNew: item?.is_new === true,
    added: false,
    removedNow: false,
});

const SortableRankRow = ({ item, index, total, rankValue, onRankChange, onRankCommit, onRemove, allowEdit, busy }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: item.seriesId, disabled: !allowEdit || busy });

    return (
        <div
            ref={setNodeRef}
            className={`cc-row${isDragging ? ' is-dragging' : ''}`}
            style={{ transform: CSS.Transform.toString(transform), transition }}
        >
            <button
                type="button"
                className="cc-handle"
                title={allowEdit ? 'Drag to reorder' : 'Reordering needs edit permission'}
                disabled={!allowEdit || busy}
                {...attributes}
                {...listeners}
            >
                <i className="fas fa-grip-vertical"></i>
            </button>

            <span className="cc-position-badge">#{index + 1}</span>

            <Thumb src={item.thumbnail} />

            <span className="cc-title" title={item.title}>
                {item.title || `Series #${item.seriesId}`}
                {item.publishAt && (
                    <small className="tr-golive">Live since {new Date(item.publishAt).toLocaleDateString()}</small>
                )}
            </span>

            {item.added
                ? <span className="cc-picker-badge is-picked">Added</span>
                : item.isNew && <span className="cc-picker-badge is-picked">New</span>}

            <div className="cc-position-set">
                <label htmlFor={`tr-rank-${item.seriesId}`}>Rank</label>
                <input
                    id={`tr-rank-${item.seriesId}`}
                    type="number"
                    min="1"
                    max={total}
                    value={rankValue}
                    disabled={!allowEdit || busy}
                    onChange={(e) => onRankChange(item.seriesId, e.target.value)}
                    onBlur={() => onRankCommit(item.seriesId)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            onRankCommit(item.seriesId);
                        }
                    }}
                />
            </div>

            {allowEdit && (
                <button
                    type="button"
                    className="nohc-icon-btn"
                    title="Remove from Explore"
                    aria-label={`Remove ${item.title || `series #${item.seriesId}`} from Explore`}
                    disabled={busy}
                    onClick={() => onRemove(item.seriesId)}
                >
                    <i className="fas fa-ban"></i>
                </button>
            )}
        </div>
    );
};

const TrailerRanking = () => {
    const dispatch = useDispatch();
    const { canEdit } = usePermission();
    const allowEdit = canEdit(PERM.TRAILER_RANKING);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const [rows, setRows] = useState([]);       // the Explore list, at most explore_limit
    const [pool, setPool] = useState([]);       // every other eligible series — out of Explore
    const [rankDrafts, setRankDrafts] = useState({});  // typed but not yet committed
    const [baselineIds, setBaselineIds] = useState([]);
    const [overflow, setOverflow] = useState(0);  // pushed past the limit by go-lives since the last save
    const [meta, setMeta] = useState({ configId: null, exploreLimit: 0, updatedAt: null, updatedBy: null });

    const [showPicker, setShowPicker] = useState(false);
    const [pickedId, setPickedId] = useState(null);
    const [pickPosition, setPickPosition] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');

    const applyRanking = useCallback((data) => {
        const limit = Number(data?.explore_limit) || 0;
        const items = (Array.isArray(data?.items) ? data.items : [])
            .slice()
            .sort((a, b) => Number(a?.rank) - Number(b?.rank))
            .map(fromItem)
            .filter((item) => Number.isFinite(item.seriesId));
        const removed = (Array.isArray(data?.available) ? data.available : [])
            .map(fromItem)
            .filter((item) => Number.isFinite(item.seriesId));
        const visible = items.slice(0, limit);

        setRows(visible);
        setPool([...items.slice(limit), ...removed]);
        setOverflow(Math.max(items.length - limit, 0));
        setRankDrafts({});
        setBaselineIds(visible.map((item) => item.seriesId));
        setMeta({
            configId: data?.id ?? null,
            exploreLimit: limit,
            updatedAt: data?.updated_at || null,
            updatedBy: data?.updated_by || null,
        });
    }, []);

    const fetchRanking = useCallback(() => {
        setLoading(true);
        setLoadError(null);
        return dispatch(action.getCuration(CURATION_TYPE))
            .then((res) => applyRanking(unwrap(res)))
            .catch((err) => {
                const message = err?.status === 404
                    ? 'Trailer Ranking is not set up on the server yet.'
                    : readMessage(err, 'Failed to load the trailer ranking.');
                setLoadError(message);
                toast.error(message);
            })
            .finally(() => setLoading(false));
    }, [dispatch, applyRanking]);

    useEffect(() => { fetchRanking(); }, [fetchRanking]);

    const dirty = useMemo(
        () => rows.length !== baselineIds.length || rows.some((row, i) => row.seriesId !== baselineIds[i]),
        [rows, baselineIds],
    );

    const canSave = allowEdit && (dirty || overflow > 0) && !saving;
    const removedNow = pool.filter((item) => item.removedNow);

    useEffect(() => {
        if (!dirty) return undefined;
        const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirty]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const from = rows.findIndex((r) => r.seriesId === active.id);
        const to = rows.findIndex((r) => r.seriesId === over.id);
        if (from === -1 || to === -1) return;
        setRows(arrayMove(rows, from, to));
    };

    const onRankChange = (seriesId, value) =>
        setRankDrafts((prev) => ({ ...prev, [seriesId]: value }));

    const onRankCommit = (seriesId) => {
        const raw = rankDrafts[seriesId];
        setRankDrafts((prev) => {
            const next = { ...prev };
            delete next[seriesId];
            return next;
        });
        if (raw !== undefined) setRows((prev) => moveToRank(prev, seriesId, raw));
    };

    const onRemove = (seriesId) => {
        const next = removeRow(rows, pool, seriesId);
        setRows(next.rows);
        setPool(next.pool);
    };

    // ─── Add Series picker ───────────────────────────────────────────────────

    const limit = meta.exploreLimit;
    const slotCount = Math.min(rows.length + 1, limit);
    const picked = pool.find((item) => item.seriesId === pickedId) || null;
    const willPushOut = picked && rows.length >= limit ? rows[rows.length - 1] : null;

    const results = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return term ? pool.filter((item) => item.title.toLowerCase().includes(term)) : pool;
    }, [pool, searchTerm]);

    const openPicker = () => {
        setPickedId(null);
        setPickPosition(1);
        setSearchTerm('');
        setShowPicker(true);
    };

    const closePicker = useCallback(() => setShowPicker(false), []);

    useEffect(() => {
        if (!showPicker) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') closePicker(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [showPicker, closePicker]);

    const submitPick = () => {
        if (!picked) return;
        const next = addAt(rows, pool, picked.seriesId, pickPosition, limit);
        setRows(next.rows);
        setPool(next.pool);
        closePicker();
    };

    // ─── Save / Reset ────────────────────────────────────────────────────────

    const put = (body) => Authservices.saveCuration({
        curation_type: CURATION_TYPE,
        curation_config_id: Number(meta.configId),
        ...body,
    })
        .then((res) => res.data)
        .catch((err) => Promise.reject({ ...(err?.response?.data || {}), status: err?.response?.status }));

    const afterWrite = (res, fallback) => {
        toast.success(readMessage(res, fallback));
        const saved = unwrap(res);
        if (saved && typeof saved === 'object') applyRanking(saved); else fetchRanking();
    };

    const handleSave = () => {
        if (!allowEdit) {
            toast.error("Opps! You don't have Permission.");
            return;
        }
        setSaving(true);
        put(buildSavePayload(rows, pool))
            .then((res) => afterWrite(res, 'Trailer ranking saved.'))
            .catch((err) => toast.error(readError(err, 'Failed to save the trailer ranking.')))
            .finally(() => setSaving(false));
    };

    const handleReset = () => {
        if (!allowEdit) {
            toast.error("Opps! You don't have Permission.");
            return;
        }
        Swal.fire({
            title: 'Reset to the default order?',
            text: `Explore is replaced with the newest ${limit || 'N'} series, newest go-live first, and saved immediately. Every other series is removed until added again. Unsaved edits will be lost.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--first-color)',
            cancelButtonColor: 'var(--error-color)',
            confirmButtonText: 'Yes, reset',
            customClass: { confirmButton: 'btn', cancelButton: 'btn' },
        }).then((result) => {
            if (!result.isConfirmed) return;
            setSaving(true);
            put({ reset: true })
                .then((res) => afterWrite(res, 'Trailer ranking reset.'))
                .catch((err) => toast.error(readError(err, 'Failed to reset the trailer ranking.')))
                .finally(() => setSaving(false));
        });
    };

    const blocked = saving || loading || !!loadError;

    return (
        <>
            <div className="cc-page">
                <div className="cc-toolbar">
                    <div className="cc-intro">
                        <h5>Explore Trailer Ranking</h5>
                        <p>
                            The order of trailers in the app&apos;s Explore feed — the same for every user.
                            Explore shows up to {limit || 'N'}. Add a series at a position, remove one, drag the
                            <i className="fas fa-grip-vertical mx-1"></i>
                            handle or type a rank, then Save &amp; Publish. A series that goes live lands on top automatically.
                        </p>
                    </div>

                    {allowEdit && (
                        <div className="cc-actions">
                            <button type="button" className="btn cc-btn-ghost" onClick={handleReset} disabled={blocked}>
                                <i className="fas fa-rotate-left me-2"></i>Reset to default
                            </button>
                            <button
                                type="button"
                                className="btn cc-btn-ghost"
                                onClick={openPicker}
                                disabled={blocked || pool.length === 0}
                                title={pool.length === 0 ? 'Every eligible series is already in Explore' : undefined}
                            >
                                <i className="fas fa-plus me-2"></i>Add Series
                            </button>
                            <button type="button" className="btn upgrade-btn" onClick={handleSave} disabled={!canSave}>
                                {saving
                                    ? <><i className="fas fa-spinner fa-spin me-2"></i>Saving...</>
                                    : <><i className="fas fa-save me-2"></i>Save &amp; Publish</>}
                            </button>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border" style={{ color: 'var(--first-color)' }} role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : loadError ? (
                    <div className="cc-empty">
                        <i className="fas fa-triangle-exclamation"></i>
                        <h5>{loadError}</h5>
                    </div>
                ) : (
                    <>
                        {dirty && (
                            <div className="cc-note">
                                <i className="fas fa-circle-exclamation me-2"></i>
                                Unsaved changes — click <strong className="mx-1">Save &amp; Publish</strong>
                                to make them live.
                            </div>
                        )}
                        {overflow > 0 && allowEdit && (
                            <div className="cc-note cc-note-muted">
                                <i className="fas fa-circle-info me-2"></i>
                                {overflow} {overflow === 1 ? 'series was' : 'series were'} pushed past #{limit} by new
                                go-lives and {overflow === 1 ? 'is' : 'are'} no longer in Explore. Save to confirm.
                            </div>
                        )}

                        <h6 className="nohc-heading">What users see now ({rows.length} / {limit})</h6>
                        {rows.length === 0 ? (
                            <div className="cc-empty">
                                <i className="fas fa-list-ol"></i>
                                <h5>No trailers in Explore</h5>
                                <p>A series shows up here once it is live and its trailer has finished processing, or when you add one.</p>
                            </div>
                        ) : (
                            <div className="cc-list">
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={rows.map((r) => r.seriesId)} strategy={verticalListSortingStrategy}>
                                        {rows.map((item, index) => (
                                            <SortableRankRow
                                                key={item.seriesId}
                                                item={item}
                                                index={index}
                                                total={rows.length}
                                                rankValue={rankDrafts[item.seriesId] ?? String(index + 1)}
                                                onRankChange={onRankChange}
                                                onRankCommit={onRankCommit}
                                                onRemove={onRemove}
                                                allowEdit={allowEdit}
                                                busy={saving}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            </div>
                        )}

                        {removedNow.length > 0 && (
                            <div className="cc-note cc-note-muted mt-3">
                                <i className="fas fa-ban me-2"></i>
                                Removed on Save: {removedNow.map((item) => item.title || `Series #${item.seriesId}`).join(', ')}.
                                Add one again to undo.
                            </div>
                        )}

                        <p className="cc-meta">
                            A removed series stays out of Explore until it is added again or the ranking is reset.
                            {meta.updatedAt && (
                                <> Last saved {new Date(meta.updatedAt).toLocaleString()}
                                    {meta.updatedBy ? ` by admin #${meta.updatedBy}` : ''}.</>
                            )}
                        </p>
                    </>
                )}
            </div>

            {/* Portalled to <body> with its own backdrop, like New on Hike's picker. */}
            {showPicker && createPortal(
                <>
                    <div className="nohc-backdrop" onClick={closePicker} />
                    <div className="common-popup add-series-popup active nohc-modal" role="dialog" aria-modal="true">
                        <div className="edit-heading d-flex align-items-center justify-content-between">
                            <h3>Add Series to Explore</h3>
                            <button className="common-close edit-close-btn" aria-label="Close" onClick={closePicker}>
                                <i className="fas fa-xmark"></i>
                            </button>
                        </div>

                        <div className="edit-inner nohc-picker">
                            <div className="nohc-step-label">1. Pick a position</div>
                            <div className="nohc-slots">
                                {Array.from({ length: slotCount }, (_, i) => i + 1).map((slot) => (
                                    <button
                                        type="button"
                                        key={slot}
                                        className={`nohc-slot${slot === pickPosition ? ' is-selected' : ''}`}
                                        aria-pressed={slot === pickPosition}
                                        onClick={() => setPickPosition(slot)}
                                    >
                                        #{slot}
                                    </button>
                                ))}
                            </div>
                            <p className="nohc-picker-hint">
                                Everything from this position down shifts by one.
                                {rows.length >= limit && ` Explore is full, so #${limit} is removed.`}
                            </p>

                            <div className="nohc-step-label">2. Select a series</div>
                            <div className="nohc-search">
                                <i className="fas fa-magnifying-glass"></i>
                                <input
                                    type="text"
                                    placeholder="Search series by title..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            <div className="cc-picker-list nohc-results">
                                {results.length === 0 ? (
                                    <p className="cc-picker-empty">
                                        {searchTerm.trim() ? 'No series matches that search.' : 'Every eligible series is already in Explore.'}
                                    </p>
                                ) : results.map((row) => {
                                    const selected = pickedId === row.seriesId;
                                    return (
                                        <button
                                            type="button"
                                            className={`nohc-result${selected ? ' is-selected' : ''}`}
                                            key={row.seriesId}
                                            aria-pressed={selected}
                                            onClick={() => setPickedId(selected ? null : row.seriesId)}
                                        >
                                            <Thumb src={row.thumbnail} small />
                                            <span className="cc-picker-title">{row.title || `Series #${row.seriesId}`}</span>
                                            {row.removedNow && <span className="cc-picker-badge">Removed (unsaved)</span>}
                                            <i className={`fas ${selected ? 'fa-circle-check' : 'fa-circle'} nohc-result-check`}></i>
                                        </button>
                                    );
                                })}
                            </div>

                            {willPushOut && (
                                <p className="nohc-picker-note">
                                    <i className="fas fa-ban me-2"></i>
                                    {willPushOut.title || `Series #${willPushOut.seriesId}`} (#{limit}) will be removed from Explore.
                                </p>
                            )}
                        </div>

                        <div className="nohc-picker-footer">
                            <span className="nohc-summary">
                                {picked
                                    ? <>Add <strong>{picked.title || `Series #${picked.seriesId}`}</strong> at <strong>#{pickPosition}</strong></>
                                    : 'No series selected'}
                            </span>
                            <div className="nohc-footer-actions">
                                <button type="button" className="btn cc-btn-ghost" onClick={closePicker}>Cancel</button>
                                <button type="button" className="btn upgrade-btn" onClick={submitPick} disabled={!picked}>
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                </>,
                document.body,
            )}

            <CuratedCollectionStyles />
            <NewOnHikeCinemaStyles />
            <style>{`
                .tr-golive { display: block; font-size: 11.5px; font-weight: 400; color: var(--text-color); }
            `}</style>
        </>
    );
};

export default TrailerRanking;
